import type { Category, ConsentKitConfig, VendorDefinition } from "../types";
import { BlockedItems } from "./blocked-items";
import { Blocklist } from "./blocklist";
import type { BlockingContext } from "./context";
import { installMutationGuard, sweepExisting } from "./mutation-guard";
import { installNetworkGuard } from "./network-guard";
import { installPrototypePatches } from "./prototype-patches";
import { installStorageGuard, type CookiePattern } from "./storage-guard";
import { releaseCategories, releaseItem } from "./unblock";
import { runAuditor } from "./auditor";
import type { BlockedItem } from "./blocked-items";

export interface Blocker {
  context: BlockingContext;
  release(categories: readonly Category[]): void;
  /** Single-item release, used by the "load this embed once" button. */
  releaseOne(item: BlockedItem): void;
  audit(): void;
  /** Cookie name patterns for the vendors this site enabled, used on revoke. */
  cookiePatterns: CookiePattern[];
}

/**
 * Installs every guard synchronously. Call order is deliberate:
 * setters first (they can prevent requests), then the network layer, then the observer
 * as a backstop, then a sweep of anything the parser already produced.
 */
export function installBlocking(
  config: ConsentKitConfig,
  vendors: VendorDefinition[],
  registry: VendorDefinition[],
  isGranted: (category: Category) => boolean,
): Blocker {
  const context: BlockingContext = {
    blocklist: new Blocklist(config, vendors, registry),
    items: new BlockedItems(),
    isGranted,
    debug: config.debug,
  };

  const cookiePatterns: CookiePattern[] = vendors.flatMap((vendor) =>
    vendor.cookies.map((cookie) => ({ pattern: cookie.name, category: vendor.category })),
  );

  installPrototypePatches(context);
  installNetworkGuard(context);
  installStorageGuard(context, cookiePatterns);
  installMutationGuard(context);
  sweepExisting(context);

  return {
    context,
    release: (categories) => releaseCategories(context, categories),
    releaseOne: releaseItem,
    audit: () => runAuditor(context),
    cookiePatterns,
  };
}
